import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import routes from '@/app/routes';
import { useNotification } from '@/components/notification/useNotification';
import Pagination from '@/features/tags/component/TagsPagination';
import { paginationValues } from '@/features/tags/utils/constants';
import {
  Headline,
  IconButton,
  Separator,
  Title,
  Caption,
  Button,
} from '@yaradigitallabs/ahua-react';

import { BulkImportsHistoryTable } from './component/BulkImportsHistoryTable';
import {
  downloadBulkImportFileReport,
  getBulkImportFiles,
} from './store/bulkImportsHistory.thunk';
import {
  selectBulkImportsHistoryLoading,
  selectBulkImportsHistoryRows,
} from './store/bulkImportsHistory.selectors';
import { bulkImportsHistoryPageTexts } from './types/bulkImportsHistory.types';
import type { BulkImportHistoryItem } from './types/bulkImportsHistory.types';
import { downloadBase64Csv } from './utils/bulkImportsHistory.helper';

export const BulkImportsHistory = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const notify = useNotification();
  const rows = useAppSelector(selectBulkImportsHistoryRows);
  const loading = useAppSelector(selectBulkImportsHistoryLoading);
  const pagination = useAppSelector(state => state.bulkImportsHistory.query);
  const count = useAppSelector(state => state.bulkImportsHistory.count);

  useEffect(() => {
    dispatch(
      getBulkImportFiles({
        page: pagination.page ?? 1,
        size: pagination.size ?? 10,
      })
    );
  }, [dispatch, pagination.page, pagination.size]);

  const handlePaginationChange = useCallback(
    ({ page, size }: { page: number; size: number }) => {
      dispatch(getBulkImportFiles({ page, size }));
    },
    [dispatch]
  );

  const handleDownloadReport = useCallback(
    async (row: BulkImportHistoryItem) => {
      try {
        const response = await dispatch(
          downloadBulkImportFileReport({ fileId: row.id })
        ).unwrap();

        downloadBase64Csv(response, row.fileName);
      } catch {
        notify('Failed to download bulk import report.', 'destructive');
      }
    },
    [dispatch, notify]
  );

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  return (
    <div className="w-full">
      <div className="flex flex-row py-1 px-8 gap-2 items-center">
        <IconButton
          className="p-0"
          size="xs"
          icon="ArrowLeft"
          variant="ghost"
          onClick={() => navigate(routes.LIST_TAGS)}
        />
        <Headline size="2xs">
          {bulkImportsHistoryPageTexts.BACK_TO_SOFT_TAGS_SEARCH}
        </Headline>
      </div>
      <Separator
        height="100%"
        mode="light"
        orientation="horizontal"
        width="100%"
      />
      <div className="flex justify-between items-center py-2 px-4">
        <div>
          <Title size="n">
            {bulkImportsHistoryPageTexts.BULK_IMPORTS_HISTORY}
          </Title>
          <Caption size="s">
            {bulkImportsHistoryPageTexts.TRACK_AND_REVIEW_BULK_SOFT_TAG_IMPORTS}
          </Caption>
        </div>
        <div>
          <Button
            onClick={handleRefresh}
            size="s"
            colorConcept="brand"
            variant="primary"
          >
            Refresh
          </Button>
        </div>
      </div>
      <div className="px-4 py-3">
        <BulkImportsHistoryTable
          rows={rows}
          loading={loading}
          onDownloadReport={handleDownloadReport}
        />
        <Pagination
          limitOptions={paginationValues}
          loading={loading}
          dataCount={count}
          paginatedData={{
            size: pagination.size ?? 10,
            page: pagination.page ?? 1,
          }}
          setPaginatedData={handlePaginationChange}
        />
      </div>
    </div>
  );
};
